<template>
  <div class="w-full h-full">
    <svg
      ref="chartSvg"
      class="w-full h-full"
      viewBox="0 0 800 300"
      preserveAspectRatio="xMidYMid meet"
    >
      <!-- Background Grid -->
      <defs>
        <pattern id="grid" width="40" height="30" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 30" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1"/>
        </pattern>
        
        <!-- Gradients for the areas -->
        <linearGradient id="userGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:rgb(59, 130, 246);stop-opacity:0.8" />
          <stop offset="100%" style="stop-color:rgb(59, 130, 246);stop-opacity:0.1" />
        </linearGradient>
        
        <linearGradient id="subscriberGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:rgb(168, 85, 247);stop-opacity:0.8" />
          <stop offset="100%" style="stop-color:rgb(168, 85, 247);stop-opacity:0.1" />
        </linearGradient>
      </defs>

      <!-- Grid -->
      <rect width="100%" height="100%" fill="url(#grid)" />

      <!-- Chart Area -->
      <g v-if="chartData.length > 0" :transform="`translate(${margin.left}, ${margin.top})`">
        <!-- Primary Y-axis (Users) labels and lines -->
        <g v-for="(tick, index) in yTicksUsers" :key="`y-users-${index}`">
          <line 
            :x1="0" 
            :y1="yScaleUsers(tick)" 
            :x2="chartWidth" 
            :y2="yScaleUsers(tick)" 
            stroke="rgba(59, 130, 246, 0.2)" 
            stroke-width="1"
          />
          <text 
            :x="-10" 
            :y="yScaleUsers(tick) + 4" 
            text-anchor="end" 
            class="text-xs fill-blue-400 font-medium"
          >
            {{ formatNumber(tick) }}
          </text>
        </g>

        <!-- Secondary Y-axis (Subscribers) labels -->
        <g v-for="(tick, index) in yTicksSubscribers" :key="`y-subs-${index}`">
          <text 
            :x="chartWidth + 10" 
            :y="yScaleSubscribers(tick) + 4" 
            text-anchor="start" 
            class="text-xs fill-purple-400 font-medium"
          >
            {{ formatSubscriberLabel(tick) }}
          </text>
        </g>

        <!-- Y-axis titles -->
        <text 
          :x="-40" 
          :y="chartHeight / 2" 
          text-anchor="middle" 
          class="text-sm fill-blue-400 font-semibold"
          transform="rotate(-90, -40, 150)"
        >
          Users
        </text>
        <text 
          :x="chartWidth + 40" 
          :y="chartHeight / 2" 
          text-anchor="middle" 
          class="text-sm fill-purple-400 font-semibold"
          transform="rotate(90, 760, 150)"
        >
          Subscribers
        </text>

        <!-- X-axis labels -->
        <g v-for="(point, index) in chartData" :key="`x-${index}`">
          <text 
            :x="xScale(index)" 
            :y="chartHeight + 20" 
            text-anchor="middle" 
            class="text-xs fill-white/70 font-medium"
          >
            {{ point.monthName.split(' ')[0] }}
          </text>
          <text 
            :x="xScale(index)" 
            :y="chartHeight + 35" 
            text-anchor="middle" 
            class="text-xs fill-white/50"
          >
            {{ point.monthName.split(' ')[1] }}
          </text>
        </g>

        <!-- Subscriber Area -->
        <path
          v-if="subscriberPath"
          :d="subscriberAreaPath"
          fill="url(#subscriberGradient)"
          class="transition-all duration-1000 ease-out"
        />

        <!-- User Area -->
        <path
          v-if="userPath"
          :d="userAreaPath"
          fill="url(#userGradient)"
          class="transition-all duration-1000 ease-out"
        />

        <!-- Subscriber Line -->
        <path
          v-if="subscriberPath"
          :d="subscriberPath"
          fill="none"
          stroke="rgb(168, 85, 247)"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="transition-all duration-1000 ease-out"
          :stroke-dasharray="subscriberLineLength"
          :stroke-dashoffset="animateLines ? subscriberLineLength : 0"
        />

        <!-- User Line -->
        <path
          v-if="userPath"
          :d="userPath"
          fill="none"
          stroke="rgb(59, 130, 246)"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="transition-all duration-1000 ease-out"
          :stroke-dasharray="userLineLength"
          :stroke-dashoffset="animateLines ? userLineLength : 0"
        />

        <!-- Data Points -->
        <g v-for="(point, index) in chartData" :key="`point-${index}`">
          <!-- User Points -->
          <circle
            :cx="xScale(index)"
            :cy="yScaleUsers(point.userCount)"
            r="4"
            fill="rgb(59, 130, 246)"
            stroke="white"
            stroke-width="2"
            class="transition-all duration-300 hover:r-6 cursor-pointer"
            @mouseenter="showTooltip($event, point, 'users')"
            @mouseleave="hideTooltip"
          />
          
          <!-- Subscriber Points -->
          <circle
            :cx="xScale(index)"
            :cy="yScaleSubscribers(point.subscriberCount)" 
            r="4"
            fill="rgb(168, 85, 247)"
            stroke="white"
            stroke-width="2"
            class="transition-all duration-300 hover:r-6 cursor-pointer"
            @mouseenter="showTooltip($event, point, 'subscribers')"
            @mouseleave="hideTooltip"
          />
        </g>


      </g>

      <!-- No Data Message -->
      <g v-else>
        <text x="400" y="150" text-anchor="middle" class="text-lg fill-white/50">
          No data available
        </text>
      </g>
    </svg>

    <!-- Tooltip -->
    <div
      v-if="tooltip.show"
      :style="{
        position: 'absolute',
        left: tooltip.x + 'px',
        top: tooltip.y + 'px',
        transform: 'translate(-50%, -100%)'
      }"
      class="bg-black/90 text-white px-3 py-2 rounded-lg text-sm border border-white/20 pointer-events-none z-50"
    >
      <div class="font-semibold">{{ tooltip.month }}</div>
      <div v-if="tooltip.type === 'users'" class="text-blue-400">
        {{ tooltip.value }} total users
      </div>
      <div v-else class="text-purple-400">
        {{ formatNumber(tooltip.value) }} total subscribers
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GrowthChart',
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      margin: {
        top: 20,
        right: 80,
        bottom: 50,
        left: 80
      },
      animateLines: true,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
        month: '',
        value: 0,
        type: ''
      }
    }
  },
  computed: {
    chartData() {
      return this.data || []
    },
    chartWidth() {
      return 800 - this.margin.left - this.margin.right
    },
    chartHeight() {
      return 300 - this.margin.top - this.margin.bottom
    },
    maxUsers() {
      if (this.chartData.length === 0) return 0
      return Math.max(...this.chartData.map(d => d.userCount))
    },
    maxSubscribers() {
      if (this.chartData.length === 0) return 0
      return Math.max(...this.chartData.map(d => d.subscriberCount))
    },
    yTicksUsers() {
      const max = this.maxUsers
      const step = Math.ceil(max / 5) || 1
      return Array.from({ length: 6 }, (_, i) => i * step)
    },
    yTicksSubscribers() {
      const max = this.maxSubscribers
      const step = Math.ceil(max / 5) || 1
      return Array.from({ length: 6 }, (_, i) => i * step)
    },
    xScale() {
      return (index) => (index / Math.max(this.chartData.length - 1, 1)) * this.chartWidth
    },
    yScaleUsers() {
      return (value) => this.chartHeight - (value / Math.max(this.maxUsers, 1)) * this.chartHeight
    },
    yScaleSubscribers() {
      return (value) => this.chartHeight - (value / Math.max(this.maxSubscribers, 1)) * this.chartHeight
    },
    userPath() {
      if (this.chartData.length === 0) return ''
      
      const points = this.chartData.map((d, i) => 
        `${i === 0 ? 'M' : 'L'} ${this.xScale(i)} ${this.yScaleUsers(d.userCount)}`
      ).join(' ')
      
      return points
    },
    subscriberPath() {
      if (this.chartData.length === 0) return ''
      
      const points = this.chartData.map((d, i) => 
        `${i === 0 ? 'M' : 'L'} ${this.xScale(i)} ${this.yScaleSubscribers(d.subscriberCount)}`
      ).join(' ')
      
      return points
    },
    userAreaPath() {
      if (this.chartData.length === 0) return ''
      
      const linePath = this.userPath
      const firstX = this.xScale(0)
      const lastX = this.xScale(this.chartData.length - 1)
      const bottom = this.chartHeight
      
      return `${linePath} L ${lastX} ${bottom} L ${firstX} ${bottom} Z`
    },
    subscriberAreaPath() {
      if (this.chartData.length === 0) return ''
      
      const linePath = this.subscriberPath
      const firstX = this.xScale(0)
      const lastX = this.xScale(this.chartData.length - 1)
      const bottom = this.chartHeight
      
      return `${linePath} L ${lastX} ${bottom} L ${firstX} ${bottom} Z`
    },
    userLineLength() {
      if (!this.$refs.chartSvg) return 0
      // Approximate line length for animation
      return this.chartWidth * 1.2
    },
    subscriberLineLength() {
      if (!this.$refs.chartSvg) return 0
      // Approximate line length for animation
      return this.chartWidth * 1.2
    }
  },
  mounted() {
    // Trigger line animation after component mounts
    setTimeout(() => {
      this.animateLines = false
    }, 100)
  },
  methods: {
    formatYLabel(value) {
      if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K'
      }
      return value.toString()
    },
    formatSubscriberLabel(value) {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M'
      }
      if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K'
      }
      return value.toString()
    },
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    },
    showTooltip(event, point, type) {
      const rect = this.$el.getBoundingClientRect()
      this.tooltip = {
        show: true,
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        month: point.monthName,
        value: type === 'users' ? point.userCount : point.subscriberCount,
        type
      }
    },
    hideTooltip() {
      this.tooltip.show = false
    }
  }
}
</script>

<style scoped>
.transition-all {
  transition: all 0.3s ease;
}
</style> 